import React, { useEffect, useState } from "react";
import { getOrders } from "../services/getOrders";
import { useNavigate, useParams } from "react-router-dom";
import {
  Snackbar,
  Alert,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import { fetchProductById } from "../services/getProducts.js";
import { addProductToOrder } from "../services/posOrders.js";
import { getVariantsWithValues } from "../services/getVariants.js";
import { getInsigniaLink } from "../services/getInsigniaLinks.js";
import usePreventZoom from "../usePreventZoom";
import { aceternitySelectSx, aceternityMenuPaperSx } from "../components/AceternityForm/aceternityTextFieldSx";
import "../components/AceternityForm/AceternityForm.css";
import "./ProductView.css";

// Frappe guarda la descripción como HTML (campo Text Editor); acá se
// extrae solo el texto para no mostrar las etiquetas literalmente.
function stripHtml(html) {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

const ProductView = () => {
  const { id } = useParams();
  const productId = id.split("-")[0];
  const navigate = useNavigate();
  const insigniaPage = getInsigniaLink(productId);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(0);
  const [precioTotal, setPrecioTotal] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [user, setUser] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [variants, setVariants] = useState([]);
  const [hasVariants, setHasVariants] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  usePreventZoom();

  // Get available quantity based on variant selection
  const getQuantityAvailable = () => {
    if (hasVariants && selectedVariant) {
      return Math.max(
        ...selectedVariant.stock.map((item) => parseInt(item.real) || 0)
      );
    }
    return product.stock_reel.length > 0
      ? Math.max(...product.stock_reel.map((item) => parseInt(item.real) || 0))
      : 0;
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (user?.id) {
      getOrders(user.id).then((result) => {
        if (result.draftOrders.length > 0) {
          setOrderId(result.draftOrders[0].orderId);
        }
      });
    }
  }, [user]);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const data = await fetchProductById(productId);
        setProduct(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
  }, [productId]);

  useEffect(() => {
    const fetchVariants = async () => {
      try {
        const result = await getVariantsWithValues(productId);
        if (result !== false) {
          setVariants(result);
          setHasVariants(true);
        } else {
          setHasVariants(false);
          setVariants([]);
        }
      } catch (error) {
        console.error("Error obteniendo variantes:", error);
        setHasVariants(false);
      }
    };

    if (product) {
      fetchVariants();
    }
  }, [product, productId]);

  const handleVariantChange = (event) => {
    const variant = variants.find((v) => v.value === event.target.value);
    setSelectedVariant(variant);
  };

  if (loading || isLoading) {
    return (
      <>
        <Header />
        <div className="product-page" style={{ textAlign: "center" }}>Cargando...</div>
        <Footer />
      </>
    );
  }
  if (error || !product) {
    return (
      <>
        <Header />
        <div className="product-page" style={{ textAlign: "center" }}>Producto no encontrado</div>
        <Footer />
      </>
    );
  }

  const quantityAvailable = getQuantityAvailable();

  const getUnitPrice = () => {
    if (hasVariants && selectedVariant) {
      return Number(product.price) + Number(selectedVariant.variation_price);
    }
    return Number(product.price);
  };

  const handleChangeQuantity = (event) => {
    let value = parseInt(event.target.value, 10);
    if (isNaN(value) || value < 1) value = 0;
    else if (value > quantityAvailable) value = quantityAvailable;
    setQuantity(value);
    setPrecioTotal(getUnitPrice() * value);
  };

  const handleAddToCart = async () => {
    if (!user) {
      alert("Debe iniciar sesión");
      return;
    }
    if (!orderId) {
      alert("Debe iniciar sesión");
      return;
    }
    if (quantity <= 0) {
      alert("La cantidad debe ser mayor a 0");
      return;
    }

    const productToAddId =
      hasVariants && selectedVariant
        ? selectedVariant.fk_product_child || productId
        : productId;

    try {
      await addProductToOrder(orderId, {
        id: productToAddId,
        qty: quantity,
        subprice: getUnitPrice(),
      });
      setOpenSnackbar(true);
      setTimeout(() => navigate("/catalogo"), 2000);
    } catch (error) {
      console.error("Error al añadir producto a la orden:", error);
    }
  };

  return (
    <>
      <Header />
      <div className="product-page">
        <div className="product-block">
          {/* Product Picture & More Info */}
          <div className="product-pic-wrap">
            <img className="product-pic" src={product.image} alt={product.label} />
            {insigniaPage.success && (
              <button
                className="product-view-more"
                onClick={() => navigate(insigniaPage.link)}
              >
                MÁS INFORMACIÓN
              </button>
            )}
          </div>

          {/* Product Info */}
          <div className="product-info">
            <h1 className="product-name">{product.label}</h1>
            <p className="product-description">{stripHtml(product.description)}</p>

            {hasVariants && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  displayEmpty
                  value={selectedVariant?.value || ""}
                  onChange={handleVariantChange}
                  sx={aceternitySelectSx}
                  MenuProps={{ PaperProps: { sx: aceternityMenuPaperSx } }}
                >
                  <MenuItem value="" disabled>
                    Seleccionar Variante
                  </MenuItem>
                  {variants.map((variant, index) => (
                    <MenuItem key={index} value={variant.value}>
                      {variant.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <div className="product-stock">
              <p className="product-stock-title">Stock:</p>
              <ul className="product-stock-list">
                {(hasVariants && selectedVariant
                  ? selectedVariant.stock
                  : product.stock_reel
                ).map((item, idx) => (
                  <li className="product-stock-item" key={idx}>
                    <span className="product-stock-item-label">{item.label}</span>
                    <span className="product-stock-item-value">{item.real}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Shopping Cart / Add to Cart */}
          <div className="product-cart-card">
            <h2 className="product-cart-title">Añadir al carrito</h2>
            <p className="product-price-row">
              Precio Unitario: <strong>₡{getUnitPrice().toFixed(0)}</strong>
            </p>
            {hasVariants && selectedVariant && (
              <span className="product-variant-caption">
                (Precio base: ₡{Number(product.price).toFixed(0)} + Variación: ₡
                {Number(selectedVariant.variation_price).toFixed(0)})
              </span>
            )}
            <div className="product-qty-row">
              <label>Cantidad:</label>
              <input
                className="product-qty-input aceternity-input"
                type="number"
                value={quantity}
                onChange={handleChangeQuantity}
              />
            </div>
            <div className="product-total-row">
              <span>Precio total:</span>
              <span className="product-total-value">₡{precioTotal}</span>
            </div>
            <div className="product-cart-actions">
              <button
                className="aceternity-btn-secondary"
                onClick={() => navigate("/catalogo")}
              >
                Volver
              </button>
              <button className="aceternity-btn-primary" onClick={handleAddToCart}>
                {user ? "Añadir" : "Iniciar sesión para comprar"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert severity="success">Producto añadido al carrito</Alert>
      </Snackbar>
      <Footer />
    </>
  );
};

export default ProductView;
